/**
 * Where each meeting of a day sits on the calendar's time grid (PRD 67, 2026-10-14).
 *
 * Vladimir: *"the calendar don't show when meetings are at the same time or
 * going in parallel"*. Meetings that overlap — directly or through a chain of
 * others — form one group; inside it each takes the first column that is free
 * by its start, and every member of the group shares the group's column count,
 * so two meetings at 19:00 are two half-width blocks side by side.
 */
export interface Span {
  key: string;
  /** Milliseconds since the epoch. */
  start: number;
  end: number;
}

export interface Placed<T extends Span = Span> {
  span: T;
  /** Which column of its group, from 0. */
  col: number;
  /** How many columns its group needs. */
  cols: number;
  /** Which group of overlapping meetings it belongs to, in time order. */
  group: number;
}

export function placeDay<T extends Span>(spans: T[]): Placed<T>[] {
  const sorted = [...spans].sort(
    (a, b) => a.start - b.start || b.end - a.end || a.key.localeCompare(b.key),
  );
  const out: Placed<T>[] = [];
  let members: Placed<T>[] = [];
  let ends: number[] = [];
  let reach = -Infinity;
  let group = -1;
  const close = () => {
    for (const p of members) p.cols = ends.length;
    out.push(...members);
    members = [];
    ends = [];
  };
  for (const span of sorted) {
    if (span.start >= reach) {
      close();
      group += 1;
    }
    let col = ends.findIndex((end) => end <= span.start);
    if (col === -1) {
      col = ends.length;
      ends.push(span.end);
    } else {
      ends[col] = span.end;
    }
    members.push({ span, col, cols: 0, group });
    reach = Math.max(reach, span.end);
  }
  close();
  return out;
}
